import type { GridApi, ICellRendererParams } from 'ag-grid-community'

import { Badge, BadgeVariant } from '@moment/design-system-ui/badge'

import Button, { ButtonVariant } from '@/components/atomic/button'
import Icon, { IconName } from '@/components/atomic/icon'
import { ClientSideAllocationCheckStatus } from '@/trading/trade-desk-blotter/bulk-allocation-upload/types'
import {
  AllocationCheckLevel,
  type Check,
} from '@/trading/types/api/edit-checks'

import { AllocationCheckColumnId } from './types'

export const getAllocationCheckValidationStatusCellRenderer = (
  params: ICellRendererParams,
) => {
  const status = params.data.manualStatus ?? params.data.serverStatus
  const getVariant = (status: ClientSideAllocationCheckStatus) => {
    switch (status) {
      case ClientSideAllocationCheckStatus.Review:
        return BadgeVariant.Warning
      case ClientSideAllocationCheckStatus.Approved:
        return BadgeVariant.Success
      case ClientSideAllocationCheckStatus.Rejected:
        return BadgeVariant.Danger
      default:
        return BadgeVariant.Neutral
    }
  }

  const getLabel = (status: ClientSideAllocationCheckStatus) => {
    if (status === ClientSideAllocationCheckStatus.Review) {
      return 'Review required'
    }
    return status
  }

  return (
    <Badge className="capitalize" variant={getVariant(status)}>
      {getLabel(status)}
    </Badge>
  )
}

export const getAllocationCheckRejectReasonCellRenderer = (
  params: ICellRendererParams,
) => {
  const checks = [...(params?.data?.checks ?? [])] as Check[]
  const status =
    params?.data?.manualStatus ??
    (params?.data?.serverStatus as ClientSideAllocationCheckStatus)
  const { error_message } = params?.data ?? {}
  const getVariant = (
    level: Check['level'],
    status: ClientSideAllocationCheckStatus,
  ) => {
    if (status === ClientSideAllocationCheckStatus.Approved) {
      return BadgeVariant.Neutral
    }
    switch (level) {
      case AllocationCheckLevel.HARD:
        return BadgeVariant.Danger
      case AllocationCheckLevel.SOFT:
        return BadgeVariant.Warning
      default:
        return BadgeVariant.Neutral
    }
  }

  const getIcon = (level: Check['level']) => {
    switch (level) {
      case AllocationCheckLevel.HARD:
        return IconName.NoSymbolIcon
      case AllocationCheckLevel.SOFT:
        return IconName.ExclamationCircleIcon
      default:
        return IconName.NoSymbolIcon
    }
  }

  if (error_message) {
    checks.push({
      level: AllocationCheckLevel.HARD,
      reason: error_message,
    })
  }

  return (
    <div className="w-full flex gap-3">
      {checks.map((check, index) => (
        <Badge
          key={`${check.reason}-${index}`}
          className="capitalize"
          variant={getVariant(check.level, status)}
        >
          <Icon iconName={getIcon(check.level)} size="small" outline />
          {check.reason}
        </Badge>
      ))}
    </div>
  )
}

export const getAllocationCheckActionCellRenderer = (
  params: ICellRendererParams,
  meta?: {
    onClickApprove?: (id: string) => void
    onClickReject?: (id: string) => void
  },
) => {
  const { serverStatus, manualStatus, subfirm } = params.data ?? {}
  const status = manualStatus ?? serverStatus

  const handleApprove = () => {
    meta?.onClickApprove?.(params.data.client_allocation_id)
  }

  const handleReject = () => {
    meta?.onClickReject?.(params.data.client_allocation_id)
  }

  return (
    <div
      className="w-fit h-full flex gap-2 items-center"
      style={{ pointerEvents: 'auto' }}
    >
      <Button
        variant={ButtonVariant.Primary}
        onClick={handleApprove}
        onMouseDown={(e) => e.stopPropagation()}
        className="min-w-7 h-7"
        icon={<Icon iconName={IconName.CheckIcon} size="small" />}
        disabled={
          !subfirm || status === ClientSideAllocationCheckStatus.Approved
        }
      />
      <Button
        variant={ButtonVariant.Danger}
        onClick={handleReject}
        onMouseDown={(e) => e.stopPropagation()}
        className="min-w-7 h-7"
        icon={<Icon iconName={IconName.NoSymbolIcon} size="small" />}
        disabled={
          !subfirm || status === ClientSideAllocationCheckStatus.Rejected
        }
      />
    </div>
  )
}

export const adaptMinWidth = (gridApi: GridApi) => {
  const rejectReasonColumn = gridApi.getColumn(
    AllocationCheckColumnId.REJECT_REASON,
  )
  if (rejectReasonColumn && rejectReasonColumn.getActualWidth() < 300) {
    gridApi.setColumnWidths([
      { key: AllocationCheckColumnId.REJECT_REASON, newWidth: 300 },
    ])
  }
}
